import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useDocumentsFolderSave } from "@/queries/use-documents.query.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  buildFolderSchema,
  FolderSchema,
} from "@/components/documents/widgets/document-folder.schema.ts";
import { ApiFolder, ApiFolderType, getDocumentFolderRuleGroups } from "@/types/document.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { DocumentFolderRolesModal } from "@/components/documents/widgets/DocumentFolderRolesModal.tsx";
import { Button, Input, Modal, ModalProps, Stack, Title, useModals } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface DocumentFolderEditModalProps extends ModalProps {
  folder: ApiFolder | null;
  type: ApiFolderType;
}

export function DocumentFolderEditModal({ folder, type, ...props }: DocumentFolderEditModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close, openModal } = useModals();
  const viewConditionTypes = useRuleConditionTypes(RuleRegistryPlace.documentFolderView);
  const editConditionTypes = useRuleConditionTypes(RuleRegistryPlace.documentFolderEdit);

  const folderType = folder ? folder.type : type;
  const isOrgType = folderType === ApiFolderType.organization;

  const folderSchemaObject = buildFolderSchema();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FolderSchema>({
    mode: "onSubmit",
    resolver: zodResolver(folderSchemaObject),
    values: {
      title: folder?.title ?? "",
      view_documents_rule_groups: getDocumentFolderRuleGroups(folder, "view_documents_rules").map(
        (group) => ({
          rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
        })
      ),
      edit_documents_rule_groups: getDocumentFolderRuleGroups(folder, "edit_documents_rules").map(
        (group) => ({
          rules: group.rules.map((rule) => ({ ...rule, value_ids: rule.value_ids ?? [] })),
        })
      ),
    },
  });

  const folderSave = useDocumentsFolderSave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const saved = await folderSave.mutateAsync({
        id: folder?.id,
        payload: {
          title: data.title,
          type: folderType,
          view_documents_rule_groups: data.view_documents_rule_groups.filter(
            (group) => group.rules.length > 0
          ),
          edit_documents_rule_groups: data.edit_documents_rule_groups.filter(
            (group) => group.rules.length > 0
          ),
        },
      });

      if (folder) {
        analyticsService.trackEvent(analyticEvents.documents.folderEdited, {
          [analyticProperties.id]: folder.id,
        });
        close(id);
      } else {
        analyticsService.trackEvent(analyticEvents.documents.folderCreated, {
          [analyticProperties.id]: saved.id,
        });
        close(id);
        if (folderType === ApiFolderType.employee) {
          openModal(DocumentFolderRolesModal, { folder: saved });
        }
      }
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("core:folder")}
      size={isOrgType ? "lg" : "md"}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={folderSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"lg"}>
          <Input
            label={t("common:title")}
            required={true}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />
          {isOrgType && (
            <>
              <Stack gap={"md"}>
                <Title
                  header={t("core:view_documents_rules")}
                  caption={t("core:view_documents_rules_caption")}
                  size={"md"}
                  paddingTop={true}
                />
                <RuleGroupsEditor
                  control={control}
                  fieldName={"view_documents_rule_groups"}
                  setValue={setValue}
                  place={RuleRegistryPlace.documentFolderView}
                  error={errors.view_documents_rule_groups?.message}
                  conditionTypes={viewConditionTypes.conditionTypes}
                  maxCardsPerRow={2}
                />
              </Stack>

              <Stack gap={"md"}>
                <Title
                  header={t("core:edit_documents_rules")}
                  caption={t("core:edit_documents_rules_caption")}
                  size={"md"}
                  paddingTop={true}
                />
                <RuleGroupsEditor
                  control={control}
                  fieldName={"edit_documents_rule_groups"}
                  setValue={setValue}
                  place={RuleRegistryPlace.documentFolderEdit}
                  error={errors.edit_documents_rule_groups?.message}
                  conditionTypes={editConditionTypes.conditionTypes}
                  maxCardsPerRow={2}
                />
              </Stack>
            </>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
